import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

interface VideoPayload {
  video?: {
    url: string;
    content_type?: string;
    file_name?: string;
    file_size?: number;
  };
}

interface StatusResponse {
  status: string;
  payload?: VideoPayload;
}

interface VideoItem {
  _id: string;
  userId: string;
  modelId: string;
  status: string;
  statusResponse?: StatusResponse;
  isGenerated: boolean;
}

interface RampWalkSocketProps {
  status: string | null;
  responseData: VideoItem | null;
  sendMessage: (message: any) => void;
  reconnectWebSocket: (modelId?: string) => void;
  arrayofResponseData: any[];
}

const RampWalkSocket = createContext<RampWalkSocketProps | undefined>(undefined);

export const RampWalkProvider: React.FC<{ 
  userId: string; 
  modelId: string; 
  children: React.ReactNode; 
  mediaType: string 
}> = ({ userId, modelId, children, mediaType }) => {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);

  const [status, setStatus] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<VideoItem | null>(null);
  const [arrayofResponseData, setArrayofResponseData] = useState<any[]>([]);

  const socketURL = `${process.env.REACT_APP_WEB_SOCKET_URL}${mediaType}?userId=${userId}&modelId=${modelId}`;

  // Use useCallback to prevent recreating the message handler
  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    console.log("WebSocket message received:", event.data);
    try {
      const parsed = JSON.parse(event.data);
      console.log("Parsed WebSocket message:", parsed);
      
      const status = parsed?.data?.[0]?.statusResponse?.status 
          || parsed?.data?.[0]?.requestStatus 
          || parsed?.data?.[0]?.status;
      
      console.log("Status from WebSocket message:", status, parsed?.data?.[0]);
      
      if (status === "OK") {
        console.log('Status is OK, updating states', parsed?.data?.[0]);
        const newItem = parsed?.data?.[0];
        setStatus(status);
        setArrayofResponseData((prev) => {
          const exists = newItem && newItem._id && prev.some(item => item._id === newItem._id);
          if (exists) {
            return prev;
          }
          const newArray = [...prev, newItem];
          console.log("New arrayofResponseData:", newArray);
          return newArray;
        });
        // Only update responseData if not duplicate
        setResponseData(prev => {
          if (newItem && newItem._id && arrayofResponseData.some(item => item._id === newItem._id)) {
            return prev;
          }
          return newItem;
        });
      }
    } catch (err) {
      console.error("Failed to parse WebSocket message:", err);
    }
  }, [arrayofResponseData]);

  const connectWebSocket = useCallback((url?: string) => {
    const wsUrl = url || socketURL;
    console.log("Connecting to WebSocket:", wsUrl);
    
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log("WebSocket connected:", socket.url);
    };

    // Use the memoized handler
    socket.onmessage = handleWebSocketMessage;

    socket.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    // socket.onclose = (event) => {
    //   console.log("WebSocket closed:", event.code, event.reason);
    // };

    socketRef.current = socket;
  }, [socketURL, handleWebSocketMessage]);

  const closeWebSocket = useCallback(() => {
    console.log("Closing WebSocket...");
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  const reconnectWebSocket = useCallback((newModelId?: string) => {
    closeWebSocket();
    console.log("Reconnecting WebSocket...");
    const url = `${process.env.REACT_APP_WEB_SOCKET_URL}${mediaType}?userId=${userId}&modelId=${newModelId || modelId}`;
    connectWebSocket(url);
  }, [closeWebSocket, connectWebSocket, mediaType, userId, modelId]);

   useEffect(() => {
      if (!userId || !modelId) return;
      connectWebSocket();
  
    }, [userId, modelId, connectWebSocket]);

  const sendMessage = useCallback((message: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn("WebSocket is not open. Message not sent:", message);
    }
  }, []);

  console.log("WebSocket context initialized with URL:", arrayofResponseData, responseData);

  return (
    <RampWalkSocket.Provider
      value={{
        status,
        sendMessage,
        responseData,
        reconnectWebSocket,
        arrayofResponseData
      }}
    >
      {children}
    </RampWalkSocket.Provider>
  );
};

export const useRambWalkWebSocket = () => {
  const context = useContext(RampWalkSocket);
  if (!context) {
    throw new Error("useMultipleWebSocket must be used within a MultiWebSocketProvider");
  }
  return context;
};